import { httpServiceClient } from '@/infra/http'
import { makeApiUrl } from '@/main/factories/http/api-url-factory'
import { clearAuthData, redirectToLogin } from './login-service-utils'
import {
  EmailDomainCheckRequest,
  EmailDomainCheckResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  LoginPasswordRequest,
  AuthenticateResponse,
  LoginWithMFARequest,
  LoginWithVerificationCodeRequest,
  VerifyCodeResponse,
  ResendCodeRequest,
  ResendCodeResponse
} from './login-service-types'
import { AccountModel } from '@/domain/models/account/account-model'
import { AccountAnonymous } from '@/domain/usecases/account/account-anonymous'

const getAuthUrl = `${makeApiUrl('/v2/login')}`

export const loginService = {
  async isLoggedUser() {
    const response = await httpServiceClient<AccountModel>({
      method: 'get',
      url: makeApiUrl('/v2/user/account'),
      skipAuthRedirect: true
    })

    const isLogged = response.body?.data ? response.body.data?.id !== AccountAnonymous.ID : false

    return isLogged as boolean
  },

  async checkEmailDomain(payload: EmailDomainCheckRequest) {
    const response = await httpServiceClient<EmailDomainCheckResponse>({
      method: 'post',
      url: `${getAuthUrl}/check-domain`,
      body: {
        email: payload.username
      },
      skipAuthRedirect: true
    })

    return response.body.data as EmailDomainCheckResponse
  },

  async loginPassword(payload: LoginPasswordRequest) {
    const response = await httpServiceClient<AuthenticateResponse>({
      method: 'post',
      url: `${getAuthUrl}/authenticate`,
      body: {
        email: payload.username,
        password: payload.password,
        verificationCode: undefined,
        remember: String(payload.remember),
        recaptchaResponse: payload.recaptchaToken || ''
      },
      skipAuthRedirect: true
    })

    return response.body.data as AuthenticateResponse
  },

  async loginWithMFA(payload: LoginWithMFARequest) {
    const response = await httpServiceClient<AuthenticateResponse>({
      method: 'post',
      url: `${getAuthUrl}/authenticate`,
      body: {
        email: payload.username,
        password: '',
        verificationCode: payload.mfaCode,
        remember: String(payload.remember),
        recaptchaResponse: ''
      },
      skipAuthRedirect: true
    })

    return response.body.data as AuthenticateResponse
  },

  async loginWithVerificationCode(payload: LoginWithVerificationCodeRequest) {
    const response = await httpServiceClient<VerifyCodeResponse>({
      method: 'post',
      url: `${getAuthUrl}/verify-code`,
      body: {
        email: payload.username,
        verificationCode: payload.code,
        remember: String(payload.remember)
      },
      skipAuthRedirect: true
    })

    return response.body.data as VerifyCodeResponse
  },

  async resendCode(payload: ResendCodeRequest) {
    const response = await httpServiceClient<ResendCodeResponse>({
      method: 'post',
      url: `${getAuthUrl}/resend-code`,
      body: {
        email: payload.email
      },
      skipAuthRedirect: true
    })

    return response.body.data as ResendCodeResponse
  },

  async forgotPassword(payload: ForgotPasswordRequest) {
    const response = await httpServiceClient<ForgotPasswordResponse>({
      method: 'post',
      url: `${getAuthUrl}/forgot-password`,
      body: {
        email: payload.email,
        recaptchaResponse: payload.recaptchaToken || ''
      },
      skipAuthRedirect: true
    })

    return response.body.data as ForgotPasswordResponse
  },

  async resetPassword(payload: ResetPasswordRequest) {
    const response = await httpServiceClient<ResetPasswordResponse>({
      method: 'post',
      url: `${getAuthUrl}/reset-password`,
      body: {
        token: payload.token,
        password: payload.password,
        recaptchaResponse: payload.recaptchaToken || ''
      },
      skipAuthRedirect: true
    })

    return response.body.data as ResetPasswordResponse
  },

  async logout() {
    try {
      await httpServiceClient<void>({
        method: 'post',
        url: `${getAuthUrl}/logout`,
        skipAuthRedirect: true
      })
    } catch (error) {
      console.warn('[loginService] Logout API unavailable:', error)
    } finally {
      clearAuthData()
      redirectToLogin()
    }
  }
}
